import React, { useState, useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import ReportByCreativeComponent from '../../../../utils/ReportByCreativeComponent/ReportByCreativeComponent';
import Loading from '../../../Loading';
import DragScroll from '../../../../utils/DragScroll';

const arraysEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

export const StatisticsByCreativeTable = ({
    data: propData,
    headerName: propHeaderName,
    loading: propLoading,
    order: propOrder,
    sort: propSort,
    metrics: propMetrics,
    keysMetric: propKeysMetric,
    rowsCount: propRowsCount,
    onAction: propOnAction,
    t,
    tableWrapperClass = {}
}) => {
    const [state, setState] = useState({
        data: propData,
        headerName: propHeaderName,
        loading: propLoading,
        order: propOrder,
        sort: propSort,
        metrics: propMetrics,
        keysMetric: propKeysMetric,
        isSorting: true,
        isTableFirst: true,
        isTableLast: false
    });

    const bodyRef = useRef(null);
    const tableWrapperClasses = classNames({
        'table-responsive': true,
        'table-responsive_compact-report': true,
        ...tableWrapperClass
    });

    useEffect(() => {
        setBodyData(propData);
        new DragScroll({ className: 'table-responsive' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setState(prevState => {
            const isChangeData = propData !== prevState.data;
            const headerChanged = propHeaderName !== prevState.headerName;
            const metricsChanged = propMetrics !== prevState.metrics;
            const keysChanged = !arraysEqual(propKeysMetric, prevState.keysMetric);
            const structureChanged = headerChanged || metricsChanged || keysChanged;
            const shouldLockStructure = propLoading && structureChanged && !isChangeData;

            const nextData = shouldLockStructure ? prevState.data : (propLoading ? '' : propData);
            const nextState = {
                ...prevState,
                data: nextData,
                headerName: shouldLockStructure ? prevState.headerName : propHeaderName,
                loading: propLoading,
                order: propOrder,
                sort: propSort,
                metrics: shouldLockStructure ? prevState.metrics : propMetrics,
                keysMetric: shouldLockStructure ? prevState.keysMetric : propKeysMetric
            };

            if (shouldLockStructure) {
                return nextState;
            }

            if (propLoading) {
                bodyRef.current && (bodyRef.current.innerHTML = '');
            } else if (isChangeData) {
                setBodyData(propData);
            }

            return nextState;
        });
    }, [propData, propHeaderName, propLoading, propOrder, propSort, propMetrics, propKeysMetric, setBodyData]);

    const onUpdateSort = useCallback((event) => {
        let sort = event.target.closest('.report-table__th-sort').getAttribute('data-key');
        let order = state.order;

        if (sort === state.sort) {
            order = (order === 'desc') ? 'asc' : 'desc';
        } else {
            order = 'desc';
        }

        propOnAction({
            sort,
            order,
            page: 1
        });
    }, [state.order, state.sort, propOnAction]);

    const setBodyData = useCallback((dataString) => {
        if (!bodyRef.current) {
            return;
        }

        const html = dataString !== undefined ? dataString : state.data;
        bodyRef.current.innerHTML = html || '';
    }, [state.data]);

    const renderHeader = useCallback(() => {
        if (!state.headerName) return null;

        let groupByTitle = ReportByCreativeComponent.utils.getGroupBy();
        return (
            <thead className="bold">
                {state.headerName.map((items, i) => {
                    return (
                        <tr key={i}>
                            {items.map((item, j) => {
                                let title = 'GroupBy' === item.config.key ? groupByTitle.title : item.title;
                                return (
                                    item.visible
                                        ? (
                                            <th
                                                className={`report-table__th`}
                                                key={j}
                                                colSpan={item.colspan}
                                                rowSpan={item.rowspan}
                                            >
                                                {item.description
                                                    ? (
                                                        <span className="report-table__th-text">{title}</span>
                                                    )
                                                    : <span className="report-table__th-text">{title}</span>
                                                }
                                                {item.isSortable && state.isSorting && state.keysMetric.indexOf(item.config.key) !== -1
                                                    ? (
                                                        <span
                                                            className={`report-table__th-sort ${state.sort === item.config.key ? 'active': ''}`}
                                                            data-key={item.config.key}
                                                            onClick={onUpdateSort}
                                                        >
                                                            <i className={`fa fa-sort-amount-${state.order}`} />
                                                        </span>
                                                    )
                                                    : null
                                                }
                                            </th>
                                        )
                                        : null
                                )
                            })}
                        </tr>
                    )
                })}
            </thead>
        );
    }, [state.headerName, state.isSorting, state.keysMetric, state.sort, state.order, onUpdateSort]);

    const staticNamesTable = t?.dataTable || {};
    let metrics = state.metrics;
    if (undefined !== state.keysMetric) {
        let delta = state.keysMetric.length - metrics.length
        if (delta > 0) {
            while (delta > 0) {
                delta--;
                metrics = metrics + '0';
            }
        }
    }

    const hasRows = state.data && state.data.includes('<tr');
    const showLoadingOverlay = propLoading && hasRows;
    const showPlaceholder = !hasRows;

    return (
        <div className="loading-wrapper">
            <div className={tableWrapperClasses}>
                {showLoadingOverlay
                    ? (
                        <div className="loading-wrapper__item">
                            <Loading />
                        </div>
                    )
                    : null}
                <table className={'table report-table green-hover report-table_first'}>
                    {renderHeader()}
                    <tbody ref={bodyRef} />
                </table>
                {showPlaceholder
                    ? propLoading
                        ? <Loading center />
                        : <div className="text-center table-notfound-sign">{staticNamesTable.noData}</div>
                    : null}
            </div>
        </div>
    );
};

StatisticsByCreativeTable.propTypes = {
    data: PropTypes.array,
    headerName: PropTypes.array,
    loading: PropTypes.bool,
    order: PropTypes.string,
    sort: PropTypes.string,
    metrics: PropTypes.string,
    keysMetric: PropTypes.array,
    rowsCount: PropTypes.number,
    onAction: PropTypes.func,
    t: PropTypes.object,
    tableWrapperClass: PropTypes.object
};

StatisticsByCreativeTable.defaultProps = {
    data: [],
    headerName: [],
    loading: false,
    order: 'desc',
    sort: 'GroupBy',
    metrics: '',
    keysMetric: [],
    rowsCount: 0,
    onAction: () => {},
    tableWrapperClass: {}
};

export default withTranslation(StatisticsByCreativeTable);
